"use client";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { recentMessages } from "@/data/chat-data";
import { notifications } from "@/data/notifications";
import { mockSearchData } from "@/data/searchdata";
import {
  Bell,
  LogOut,
  Mail,
  MessageCircleQuestion,
  MessageSquare,
  Moon,
  PackageOpen,
  PanelLeftClose,
  PanelRightClose,
  Search,
  Settings,
  TrendingUp,
  User,
  X,
} from "lucide-react";
import { useTheme } from "next-themes";
import Link from "next/link";
import { usePathname, useRouter } from "next/navigation";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import ChatDrawer from "../header/ChatDrawer";
import NotificationDrawer from "../header/NotificationDrawer";
import { ThemeToggle } from "../header/ThemeToggle";
import { Badge } from "../ui/badge";
import { useSidebar } from "./sidebar-context";
import { useAuth } from "@/app/provider/auth-provider";
import { HeaderSearchResults } from "@/components/layout/header-search-results";
import { TrungThuHeaderDecor, TrungThuSeasonBadge } from "@/components/layout/trung-thu-header-decor";
import { WomensDayHeaderDecor, WomensDaySeasonBadge } from "@/components/layout/womens-day-header-decor";
import { TeachersDayHeaderDecor, TeachersDaySeasonBadge } from "@/components/layout/teachers-day-header-decor";
import { isTrungThuSeason } from "@/lib/trung-thu-season";
import { isWomensDaySeason } from "@/lib/womens-day-season";
import { isTeachersDaySeason } from "@/lib/teachers-day-season";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import type { GameSearchResult } from "@/lib/game-data/types";
import { cn } from "@/lib/utils";

export function Header() {
  const { user, logout } = useAuth();

  const displayName = user?.name
    ? user.name
    : user?.email
      ? user.email.split("@")[0]
          .replace(/\./g, " ")
          .replace(/\b\w/g, c => c.toUpperCase())
      : "Guest";

  const avatarFallback = displayName
    ? displayName.substring(0, 2).toUpperCase()
    : "GU";

  const pathname = usePathname();
  const { theme, setTheme } = useTheme();
  const router = useRouter();
  const { toggleCollapsed, collapsed } = useSidebar();
  const [isMessagesDrawerOpen, setIsMessagesDrawerOpen] = useState(false);
  const [isNotificationsDrawerOpen, setIsNotificationsDrawerOpen] = useState(false);
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);

  const [searchQuery, setSearchQuery] = useState("");
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const [gamePreview, setGamePreview] = useState<GameSearchResult[]>([]);
  const [gamePreviewLoading, setGamePreviewLoading] = useState(false);
  const searchInputRef = React.useRef<HTMLInputElement>(null);
  const mobileSearchInputRef = React.useRef<HTMLInputElement>(null);
  const searchResultsRef = React.useRef<HTMLDivElement>(null);
  const gameFetchAbortRef = useRef<AbortController | null>(null);

  const normalize = (s: string) => s.toLowerCase().trim();

  const suggestedResults = useMemo(
    () => ({
      wikiPosts: mockSearchData.wikiPosts.slice(0, 4),
      siteCategories: mockSearchData.siteCategories.slice(0, 3),
      highlights: mockSearchData.highlights.slice(0, 3),
      pages: mockSearchData.pages.slice(0, 5),
    }),
    [],
  );

  const filteredResults = useMemo(() => {
    const query = normalize(searchQuery);
    if (!query) {
      return { wikiPosts: [], siteCategories: [], highlights: [], pages: [] };
    }
    return {
      wikiPosts: mockSearchData.wikiPosts
        .filter((w) => normalize(w.title).includes(query) || normalize(w.tag).includes(query))
        .slice(0, 6),
      siteCategories: mockSearchData.siteCategories
        .filter((c) => normalize(c.name).includes(query) || normalize(c.badge).includes(query))
        .slice(0, 4),
      highlights: mockSearchData.highlights
        .filter((h) => normalize(h.name).includes(query) || normalize(h.badge).includes(query))
        .slice(0, 4),
      pages: mockSearchData.pages.filter((p) => normalize(p.title).includes(query)).slice(0, 8),
    };
  }, [searchQuery]);

  const trimQuery = searchQuery.trim();
  const shouldFetchGamePreview = trimQuery.length >= 2 || /^\d+$/.test(trimQuery);

  const hasLocalHits =
    filteredResults.wikiPosts.length > 0 ||
    filteredResults.siteCategories.length > 0 ||
    filteredResults.highlights.length > 0 ||
    filteredResults.pages.length > 0;

  const hasFilteredHits =
    trimQuery !== "" &&
    (hasLocalHits || gamePreview.length > 0 || gamePreviewLoading || shouldFetchGamePreview);

  const searchPanelMode = !trimQuery
    ? ("suggestions" as const)
    : hasFilteredHits
      ? ("filtered" as const)
      : ("empty" as const);

  const closeSearchPanels = () => {
    setIsSearchFocused(false);
    setMobileSearchOpen(false);
    setSearchQuery("");
    setGamePreview([]);
    setGamePreviewLoading(false);
    gameFetchAbortRef.current?.abort();
  };

  const goToSearchPage = useCallback(() => {
    const q = searchQuery.trim();
    if (!q) return;
    router.push(`/search?q=${encodeURIComponent(q)}`);
    closeSearchPanels();
  }, [router, searchQuery]);

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    goToSearchPage();
  };

  useEffect(() => {
    if (!shouldFetchGamePreview) {
      gameFetchAbortRef.current?.abort();
      setGamePreview([]);
      setGamePreviewLoading(false);
      return;
    }

    setGamePreviewLoading(true);
    const timer = window.setTimeout(() => {
      gameFetchAbortRef.current?.abort();
      const ac = new AbortController();
      gameFetchAbortRef.current = ac;

      fetch(`/api/game-search?q=${encodeURIComponent(trimQuery)}&preview=1`, { signal: ac.signal })
        .then((res) => (res.ok ? res.json() : null))
        .then((json: { items?: GameSearchResult[] } | null) => {
          if (ac.signal.aborted) return;
          setGamePreview(Array.isArray(json?.items) ? json.items : []);
        })
        .catch((err: unknown) => {
          if (err instanceof DOMException && err.name === "AbortError") return;
          setGamePreview([]);
        })
        .finally(() => {
          if (!ac.signal.aborted) setGamePreviewLoading(false);
        });
    }, 400);

    return () => {
      window.clearTimeout(timer);
      gameFetchAbortRef.current?.abort();
    };
  }, [trimQuery, shouldFetchGamePreview]);

  React.useEffect(() => {
    if (mobileSearchOpen && mobileSearchInputRef.current) {
      setTimeout(() => mobileSearchInputRef.current?.focus(), 100);
    }
  }, [mobileSearchOpen]);

  // Add click outside handler to close search results
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchResultsRef.current && !searchResultsRef.current.contains(event.target as Node) && searchInputRef.current && !searchInputRef.current.contains(event.target as Node)) {
        setIsSearchFocused(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    setIsMessagesDrawerOpen(false);
    setIsNotificationsDrawerOpen(false);
    setMobileSearchOpen(false);
    setIsSearchFocused(false);
  }, [pathname]);

  // Ensure only one drawer can be open at a time
  useEffect(() => {
    if (isMessagesDrawerOpen) {
      setIsNotificationsDrawerOpen(false);
    }
  }, [isMessagesDrawerOpen]);

  useEffect(() => {
    if (isNotificationsDrawerOpen) {
      setIsMessagesDrawerOpen(false);
    }
  }, [isNotificationsDrawerOpen]);

  const unreadNotificationsCount = notifications.filter((n) => !n.read).length;
  const unreadMessagesCount = recentMessages.filter((m) => m.unread).length;
  const trungThu = isTrungThuSeason();
  const teachersDay = !trungThu && isTeachersDaySeason();
  const womensDay = !trungThu && !teachersDay && isWomensDaySeason();

  const searchPanelBindings = (
    <HeaderSearchResults
      searchQuery={searchQuery}
      mode={searchPanelMode}
      filtered={filteredResults}
      suggested={suggestedResults}
      gameResults={shouldFetchGamePreview ? gamePreview : []}
      isGameLoading={shouldFetchGamePreview && gamePreviewLoading}
      onNavigate={closeSearchPanels}
      onViewAll={trimQuery && hasLocalHits ? goToSearchPage : undefined}
      onViewGameSearch={trimQuery ? goToSearchPage : undefined}
    />
  );

  const handleMessageClick = (conversationId: string) => {
    router.push(`/chat?conversation=${conversationId}`);
    setIsMessagesDrawerOpen(false);
  };

  const handleNotificationClick = () => {
    setIsNotificationsDrawerOpen(false);
    // In a real app, you would mark the notification as read here
  };

  const markAllNotificationsAsRead = () => {
    // In a real app, you would call an API to mark all notifications as read
    // For now, we'll just close the drawer
    setIsNotificationsDrawerOpen(false);
  };

  const handleLogout = async () => {
    try {
      await logout();
    } finally {
      router.push("/login");
    }
  };

  return (
    <>
      <header
        className={cn(
          "sticky top-0 z-30 flex h-16 items-center justify-between border-b px-4 md:px-6 relative overflow-hidden",
          trungThu
            ? "border-amber-500/25 bg-gradient-to-r from-amber-950/35 via-background to-rose-950/25 dark:from-amber-950/50 dark:via-background dark:to-rose-950/35"
            : teachersDay
              ? "border-emerald-500/30 bg-gradient-to-r from-emerald-950/45 via-background to-rose-950/25 dark:from-emerald-950/60 dark:via-background dark:to-rose-950/35"
              : womensDay
                ? "border-rose-500/25 bg-gradient-to-r from-rose-950/40 via-background to-pink-950/30 dark:from-rose-950/55 dark:via-background dark:to-pink-950/40"
                : "bg-background"
        )}
      >
        {trungThu && <TrungThuHeaderDecor />}
        {teachersDay && <TeachersDayHeaderDecor />}
        {womensDay && <WomensDayHeaderDecor />}
        <div className="relative z-10 flex min-w-0 flex-1 items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={toggleCollapsed}
            className={cn(
              trungThu && "text-amber-100 hover:bg-amber-500/15 hover:text-amber-50",
              teachersDay && "text-emerald-100 hover:bg-emerald-500/15 hover:text-emerald-50",
              womensDay && "text-rose-100 hover:bg-rose-500/15 hover:text-rose-50"
            )}
          >
            {collapsed ? <PanelRightClose className="!text-3xl" /> : <PanelLeftClose className="!text-3xl" />}
          </Button>

          <form
            className="relative hidden min-w-0 md:block md:w-64 lg:w-80 xl:min-w-[22rem] xl:w-[min(36rem,calc(100vw-340px))]"
            onSubmit={handleSearchSubmit}
          >
            <Search className="pointer-events-none absolute left-2.5 top-1/2 z-10 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              ref={searchInputRef}
              type="search"
              enterKeyHint="search"
              placeholder={
                trungThu
                  ? "Tìm item, NPC, wiki… 🏮 (Enter để tra cứu)"
                  : teachersDay
                    ? "Tìm item, NPC, wiki… 📚 (Enter để tra cứu)"
                    : womensDay
                      ? "Tìm item, NPC, wiki… (Enter để tra cứu)"
                      : "Tìm item, NPC, wiki… (Enter để tra cứu)"
              }
              className={cn(
                "w-full rounded-md border-0 bg-muted pl-9 pr-3 focus-visible:ring-0 focus-visible:ring-offset-0",
                trungThu && "bg-amber-950/20 ring-1 ring-amber-500/20 placeholder:text-amber-100/40 dark:bg-amber-950/30",
                teachersDay &&
                  "bg-emerald-950/25 ring-1 ring-emerald-500/25 placeholder:text-emerald-100/40 dark:bg-emerald-950/35",
                womensDay && "bg-rose-950/25 ring-1 ring-rose-500/25 placeholder:text-rose-100/40 dark:bg-rose-950/35"
              )}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onFocus={() => setIsSearchFocused(true)}
            />

            {isSearchFocused && (
              <div
                ref={searchResultsRef}
                className="absolute left-0 right-0 top-full z-[100] mt-1.5 overflow-hidden rounded-xl border bg-popover text-popover-foreground shadow-xl"
              >
                <div className="flex items-center justify-between gap-2 border-b border-border px-3 py-2">
                  <span className="truncate text-xs font-medium text-muted-foreground">
                    Wiki tsorigin.com · trang web TS Origin
                  </span>
                  <Button variant="ghost" size="icon" className="h-8 w-8 shrink-0" type="button" aria-label="Đóng ô tìm" onClick={closeSearchPanels}>
                    <X className="h-4 w-4" />
                  </Button>
                </div>
                <div className="min-h-[120px] px-2 pt-1">{searchPanelBindings}</div>
              </div>
            )}
          </form>

          <Sheet open={mobileSearchOpen} onOpenChange={setMobileSearchOpen}>
            <SheetTrigger asChild>
              <Button variant="outline" size="icon" className="shrink-0 md:hidden" type="button" aria-label="Mở tìm kiếm">
                <Search className="h-5 w-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="top" className="flex max-h-[min(90vh,640px)] flex-col gap-0">
              <SheetHeader className="space-y-1 pb-3 text-left">
                <SheetTitle>Tìm kiếm TS Origin</SheetTitle>
              </SheetHeader>
              <form className="relative shrink-0" onSubmit={handleSearchSubmit}>
                <Search className="pointer-events-none absolute left-2.5 top-1/2 z-10 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  ref={mobileSearchInputRef}
                  type="search"
                  enterKeyHint="search"
                  placeholder="Tìm item, NPC… (Enter)"
                  className="w-full rounded-md border border-input bg-background pl-9"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </form>
              <div className="min-h-0 flex-1 overflow-y-auto py-3">{searchPanelBindings}</div>
            </SheetContent>
          </Sheet>
          {trungThu && <TrungThuSeasonBadge compact className="hidden lg:inline-flex shrink-0" />}
          {teachersDay && <TeachersDaySeasonBadge compact className="hidden lg:inline-flex shrink-0" />}
          {womensDay && <WomensDaySeasonBadge compact className="hidden lg:inline-flex shrink-0" />}
        </div>

        <div className="relative z-10 flex shrink-0 items-center gap-4">
          <Button variant="outline" size="icon" className="relative" type="button" onClick={() => setIsMessagesDrawerOpen(true)} aria-label="Tin nhắn">
            <MessageSquare className="h-5 w-5" />
            {unreadMessagesCount > 0 && (
              <span className="absolute -right-0.5 -top-0.5 flex min-h-[18px] min-w-[18px] items-center justify-center rounded-full bg-red-600 px-1 text-[10px] font-semibold leading-none text-white ring-2 ring-background">
                {unreadMessagesCount > 9 ? "9+" : unreadMessagesCount}
              </span>
            )}
          </Button>

          <Button variant="outline" size="icon" className="relative" type="button" onClick={() => setIsNotificationsDrawerOpen(true)} aria-label="Thông báo">
            <Bell className="h-5 w-5" />
            {unreadNotificationsCount > 0 && (
              <span className="absolute -right-0.5 -top-0.5 flex min-h-[18px] min-w-[18px] items-center justify-center rounded-full bg-red-600 px-1 text-[10px] font-semibold leading-none text-white ring-2 ring-background">
                {unreadNotificationsCount > 9 ? "9+" : unreadNotificationsCount}
              </span>
            )}
          </Button>

          <ThemeToggle />

          <Link
            href="/dashboard/user?tab=wallet"
            className={cn(
              "flex items-center gap-2 rounded-full border px-3 py-1.5 max-sm:hidden",
              trungThu
                ? "border-amber-400/35 bg-amber-500/10 text-amber-50 hover:bg-amber-500/15"
                : teachersDay
                  ? "border-emerald-400/35 bg-emerald-500/10 text-emerald-50 hover:bg-emerald-500/15"
                  : womensDay
                    ? "border-rose-400/35 bg-rose-500/10 text-rose-50 hover:bg-rose-500/15"
                    : ""
            )}
          >
            <span className="text-sm font-medium">{(user?.point ?? 0).toLocaleString()} P</span>
            <span className={trungThu || teachersDay || womensDay ? "text-base" : "text-green-500"}>
              {trungThu ? "🥮" : teachersDay ? "📚" : womensDay ? "🎁" : "💰"}
            </span>
          </Link>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="rounded-full">
                <Avatar className="h-8 w-8">
                  <AvatarImage src="" alt={displayName} />
                  <AvatarFallback>{avatarFallback}</AvatarFallback>
                </Avatar>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-80 p-0">
              <div className="flex flex-col space-y-2 p-4">
                <div className="flex items-center gap-3">
                  <Avatar className="h-16 w-16">
                    {/* Nếu sau này có avatar_url thì thay vào đây */}
                    <AvatarImage src="" alt={displayName} />
                    <AvatarFallback>{avatarFallback}</AvatarFallback>
                  </Avatar>

                  <div>
                    <h3 className="text-lg font-semibold">
                      {displayName}
                    </h3>

                    <p className="text-sm text-muted-foreground">
                      @{displayName}
                    </p>

                    <div className="flex items-center gap-1 text-sm text-muted-foreground">
                      <Mail className="h-3 w-3" />
                      <span>{user?.email || "Chưa đăng nhập"}</span>
                    </div>
                  </div>
                </div>
              </div>

              <DropdownMenuSeparator />

              <div className="p-2">
                <DropdownMenuItem asChild className="flex gap-3 px-3 py-1.5">
                  <Link
                    href={
                      user?.name
                        ? `/profile/${encodeURIComponent(user.name.trim())}`
                        : user
                          ? `/profile/${encodeURIComponent(displayName.trim())}`
                          : "/login"
                    }
                  >
                    <div className="flex h-8 w-8 items-center justify-center rounded-md bg-blue-100">
                      <User className="h-4 w-4 text-blue-600" />
                    </div>
                    <div className="flex flex-col">
                      <span>Hồ sơ cá nhân</span>
                      <span className="text-xs text-muted-foreground">Xem trang hồ sơ công khai</span>
                    </div>
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild className="flex gap-3 px-3 py-1.5">
                  <Link href="/dashboard/user">
                    <div className="flex h-8 w-8 items-center justify-center rounded-md bg-purple-100">
                      <TrendingUp className="h-4 w-4 text-purple-600" />
                    </div>
                    <div className="flex flex-col">
                      <span>Bảng điều khiển</span>
                      <span className="text-xs text-muted-foreground">Hoạt động và thống kê của bạn</span>
                    </div>
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild className="flex gap-3 px-3 py-1.5">
                  <Link href="/list-account">
                    <div className="flex h-8 w-8 items-center justify-center rounded-md bg-blue-100">
                      <MessageCircleQuestion className="h-4 w-4 text-blue-600" />
                    </div>
                    <div className="flex flex-col">
                      <span>Tài khoản game</span>
                      <span className="text-xs text-muted-foreground">Danh sách tài khoản game đã tạo</span>
                    </div>
                  </Link>
                </DropdownMenuItem>

                <DropdownMenuItem asChild className="flex gap-3 px-3 py-1.5">
                  <Link href="/ruong-bau-origin">
                    <div className="flex h-8 w-8 items-center justify-center rounded-md bg-rose-100 dark:bg-rose-950/60">
                      <PackageOpen className="h-4 w-4 text-rose-600 dark:text-rose-300" />
                    </div>
                    <div className="flex flex-col">
                      <span>Rương báu Origin</span>
                      <span className="text-xs text-muted-foreground">Mở rương gói kỳ trân — chọn pet nhận giftcode</span>
                    </div>
                  </Link>
                </DropdownMenuItem>

                <DropdownMenuItem asChild className="flex gap-3 px-3 py-1.5">
                  <Link href="/settings">
                    <div className="flex h-8 w-8 items-center justify-center rounded-md bg-gray-100">
                      <Settings className="h-4 w-4 text-gray-600" />
                    </div>
                    <div className="flex flex-col">
                      <span>Thiết lập</span>
                      <span className="text-xs text-muted-foreground">Tùy chọn cá nhân</span>
                    </div>
                  </Link>
                </DropdownMenuItem>

                <DropdownMenuItem className="flex items-center justify-between p-3">
                  <div className="flex gap-3">
                    <div className="flex h-8 w-8 items-center justify-center rounded-md bg-purple-100">
                      <Moon className="h-4 w-4 text-purple-600" />
                    </div>
                    <div className="flex flex-col">
                      <span>Chế độ tối</span>
                      <span className="text-xs text-muted-foreground">Bật/tắt chế độ tối</span>
                    </div>
                  </div>
                  <Switch checked={theme === "dark"} onCheckedChange={() => setTheme(theme === "dark" ? "light" : "dark")} />
                </DropdownMenuItem>
              </div>

              <div className="p-4 pt-0">
                <Button variant="destructive" className="w-full justify-center" onClick={() => void handleLogout()}>
                  <LogOut className="h-4 w-4" />
                  <span>Đăng xuất</span>
                </Button>
              </div>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>

      {/* Overlay for both drawers */}
      <div
        className={`fixed inset-0 bg-black/20 z-50 transition-opacity ${isMessagesDrawerOpen || isNotificationsDrawerOpen ? "opacity-100" : "opacity-0 pointer-events-none"}`}
        onClick={() => {
          setIsMessagesDrawerOpen(false);
          setIsNotificationsDrawerOpen(false);
        }}
      />

      {/* Messages Drawer */}
      <ChatDrawer setIsMessagesDrawerOpen={setIsMessagesDrawerOpen} isMessagesDrawerOpen={isMessagesDrawerOpen} handleMessageClick={handleMessageClick} />

      {/* Notifications Drawer */}
      <NotificationDrawer handleNotificationClick={handleNotificationClick} isNotificationsDrawerOpen={isNotificationsDrawerOpen} markAllNotificationsAsRead={markAllNotificationsAsRead} setIsNotificationsDrawerOpen={setIsNotificationsDrawerOpen} />
    </>
  );
}
